// @ts-nocheck
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
// import AddToCartOptionsAndButton, {
//     AddToCartOptionsAndButtonStyles,
// } from "@store/Library/Components/Toolkit/AddToCartOptionsAndButton"; // Store Custom Code
import { formFieldLabelStyles } from "@store/Themes/common/mixins/formField";
import { productDetailsFormWrapperStyles } from "@store/Themes/common/mixins/productDetails";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

/* Store Custom Code - Start */
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import translate from "@insite/client-framework/Translate";
import AddToCartOptionsAndButton, {
    AddToCartOptionsAndButtonStyles,
} from "@store/Library/Components/Toolkit/AddToCartOptionsAndButton";
import { contentWidthStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import { ConfigurationType } from "@insite/client-framework/Services/ProductServiceV2";
import { useSelector } from "react-redux";
import { getFilteredConfigurable } from "@store/Framework/Store/Store/Data/FilteredConfigurable/FilteredConfigurableSelector";
/* Store Custom Code - End */

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    configurationSelection: state.pages.productDetails.configurationSelection,
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
});

const labelProps = {
    css: css`
        ${formFieldLabelStyles()}
        font-size: ${x => x.theme.design.typography.size.smaller};
    `,
};

export interface ProductDetailsAddToCartOptionsAndButtonStyles {
    addToCartOptionAndButton?: AddToCartOptionsAndButtonStyles;
    // Store Custom Code
    infosBox?: InfosBoxProps;
}

export const productDetailsAddToCartOptionsAndButtonStyles: ProductDetailsAddToCartOptionsAndButtonStyles = {
    addToCartOptionAndButton: {
        flexWrapper: {
            extendedStyles: {
                wrapper: {
                    css: productDetailsFormWrapperStyles(),
                },
            },
        },
        quantityOrdered: {
            extendedStyles: {
                labelProps,
                cssOverrides: {
                    inputSelect: css`
                        width: ${pxToRem(63)};
                    `,
                },
            },
        },
        unitOfMeasure: {
            extendedStyles: {
                labelProps,
            },
        },
    },
    /* Store Custom Code - Start */
    infosBox: {
        type: "info",
        extendedStyles: {
            wrapper: {
                css: css`
                    ${contentWidthStyles()}
                    ${spacingStyles(pxToRem(16), pxToRem(16))}
                `,
            },
        },
    },
    /* Store Custom Code - End */
};

const styles = productDetailsAddToCartOptionsAndButtonStyles;

const ProductDetailsAddToCartOptionsAndButton = ({
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    // Store Custom Code
    product,
}: Props) => {
    /* Store Custom Code - Start */
    const hasProductsInKit = React.useMemo(() => {
        const value = product.properties?.productsInKit;
        if (!value) {
            return false;
        }

        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) && parsed.length > 0;
        } catch {
            return false;
        }
    }, [product.properties?.productsInKit]);

    const isConfigurable =
        product.configurationType !== ConfigurationType.None && product.configurationType !== ConfigurationType.Fixed;

    const isKitProduct = hasProductsInKit && isConfigurable;

    const filteredConfigurableData = useSelector((state: ApplicationState) => getFilteredConfigurable(state, product.id));
    const hasIncompatibleSpecialUnitRestriction = !!filteredConfigurableData?.hasIncompatibleSpecialUnitRestriction;
    /* Store Custom Code - End */

    return (
        <>
            {/* Store Custom Code - Start */}
            {isKitProduct && configurationCompleted === false && (
                <InfosBox {...styles.infosBox}>{translate("Please select all required options to continue.")}</InfosBox>
            )}
            {isKitProduct && hasIncompatibleSpecialUnitRestriction && (
                <InfosBox {...styles.infosBox} type="warning">
                    {translate(
                        "The selected indoor units are not compatible with the required configuration for this special unit. Change or clear one or more indoor unit selections.",
                    )}
                </InfosBox>
            )}
            {/* Store Custom Code - End */}
            <AddToCartOptionsAndButton
                sizeVariant="default"
                buttonSizeVariant="xlarge"
                extendedStyles={{
                    ...styles.addToCartOptionAndButton,
                    unitOfMeasure: {
                        ...styles.addToCartOptionAndButton?.unitOfMeasure,
                        uid: "unit-of-measure",
                    },
                    quantityOrdered: {
                        ...styles.addToCartOptionAndButton?.quantityOrdered,
                        id: "qty-ordered",
                        configurationCompleted,
                        variantSelectionCompleted,
                    },
                    addToCartButton: {
                        ...styles.addToCartOptionAndButton?.addToCartButton,
                        configurationSelection,
                        configurationCompleted,
                        variantSelectionCompleted,
                        allowDisabledWhenConfigurationIncomplete: isKitProduct /* Store Custom Code */,
                        hasIncompatibleSpecialUnitRestriction /* Store Custom Code */,
                        "data-test-selector": "addProductToCart",
                    },
                }}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsAddToCartOptionsAndButton)),
    definition: {
        displayName: "Add to Cart Options and Button",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        icon: "cart-circle-plus",
    },
};

export default widgetModule;
